import React, { memo, useMemo, useState } from "react";
import { Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import {
  DashboardApiV2DashboardKeystatisticsGetRequest,
  DashboardFlowKeyStatistics,
} from "@generated/v2";
import { v2DashboardFlowKeyStatisticsGet } from "app/api/v2/dashboardApi";
import { useFetchData } from "app/hooks/hooks";
import DashboardItemLayout from "../DashboardItemLayout/DashboardItemLayout";
import styles from "./KeyStatistics.module.scss";
import { DashboardItemFilterComplete } from "app/types/dashboardType";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { Switch } from "app/components/Common/Switch";
import { InconclusiveRatioIcon } from "app/components/Icons/InconclusiveRatioIcon";
import { NumberOfTestFlowsIcon } from "app/components/Icons/NumberOfTestFlowsIcon";
import { NegativeTestFlowsIcon } from "app/components/Icons/NegativeTestFlowsIcon";
import { PositiveTestFlowsIcons } from "app/components/Icons/PositiveTestFlowsIcons";
import { PositiveRatioIcon } from "app/components/Icons/PositiveRatioIcon";
import { InconclusiveTestFlowsIcon } from "app/components/Icons/InconclusiveTestFlowsIcon";
import { KeyStatisticCard } from "app/screens/DashboardScreen/components/DashboardPremium/components/KeyStatistics/KeyStatisticCard";

const KeyStatistics = ({
  customerId,
  siteId,
  start,
  end,
  routes,
  period,
}: DashboardItemFilterComplete) => {
  const { t } = useTranslation();

  const [showInconclusive, setShowInconclusive] = useState(false);

  const { data, loading } = useFetchData<
    DashboardFlowKeyStatistics,
    DashboardApiV2DashboardKeystatisticsGetRequest
  >(v2DashboardFlowKeyStatisticsGet, {
    shouldCallApi: !!customerId,
    params: {
      customerId,
      siteId,
      start,
      end,
      routes,
    },
  });

  const colsLg = showInconclusive ? 8 : 6;

  const suffix = useMemo(() => {
    return (
      <Switch
        checked={showInconclusive}
        onChange={setShowInconclusive}
        title={t("dashboard.showInconclusiveFlows")}
      />
    );
  }, [showInconclusive, t]);

  return (
    <ErrorFallbackUI>
      <DashboardItemLayout
        title={t("dashboard.keyStatisticsTitle")}
        subTitle={period}
        loading={loading}
        suffix={suffix}
        info={t("dashboard.inconclusiveFlowsInfo")}
      >
        <Row type="flex" gutter={[20, 20]}>
          <Col className={styles.statisticCol} xs={24} md={12} lg={colsLg}>
            <KeyStatisticCard
              data={data}
              statistic="total"
              description={t("dashboard.totalNoTestFlows")}
              nuetralColor
              icon={<NumberOfTestFlowsIcon />}
            />
          </Col>
          <Col xs={24} md={12} lg={colsLg}>
            <KeyStatisticCard
              data={data}
              statistic="negative"
              description={t("dashboard.negativeTestFlows")}
              icon={<NegativeTestFlowsIcon />}
            />
          </Col>
          <Col xs={24} md={12} lg={colsLg}>
            <KeyStatisticCard
              data={data}
              statistic="positive"
              description={t("dashboard.positiveTestFlows")}
              reverseColors
              icon={<PositiveTestFlowsIcons />}
            />
          </Col>
          <Col xs={24} md={12} lg={colsLg}>
            <KeyStatisticCard
              data={data}
              statistic="positiveRatio"
              description={t("dashboard.positiveRatio")}
              info={t("dashboard.flowsPositiveRatioInfo")}
              reverseColors
              icon={<PositiveRatioIcon />}
            />
          </Col>
          {showInconclusive && (
            <Col xs={24} md={12} lg={colsLg}>
              <KeyStatisticCard
                data={data}
                statistic="inconclusive"
                description={t("dashboard.inconclusiveTestFlows")}
                info={t("dashboard.inconclusiveTestFlowsInfo")}
                reverseColors
                icon={<InconclusiveTestFlowsIcon />}
              />
            </Col>
          )}
          {showInconclusive && (
            <Col xs={24} md={12} lg={colsLg}>
              <KeyStatisticCard
                data={data}
                statistic="inconclusiveRatio"
                description={t("dashboard.inconclusiveRatio")}
                info={t("dashboard.inconclusiveRatioInfo")}
                reverseColors
                icon={<InconclusiveRatioIcon />}
              />
            </Col>
          )}
        </Row>
      </DashboardItemLayout>
    </ErrorFallbackUI>
  );
};

export default memo(KeyStatistics);
